import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';

import api from '@/services/api';

import Pagination from '@/utils/pagination/Pagination';

import Header from '@/views/components/Header/Header';
import Footer from '@/views/components/Footer/Footer';
import LetterTemplatesSearchFilters from '@/views/pages/Templates/elements/LetterTemplatesSearchFilters';
import LetterTemplateCardsContainer from '@/views/pages/Templates/elements/LetterTemplateCardsContainer';

import TooltipIconText from '@/views/components/TooltipIcon/TooltipIconText';

import parseObjectToQuery from '@/utils/functions/parseObjectToQuery';
import { CanHandleDepartments } from '@/utils/permissions/permissions';

import styles from '@/views/pages/Templates/Templates.module.scss';

const Templates = () => {
	const { dict, mode } = useSelector((state) => state.app);
	const { user: { role: userRole } } = useSelector((state) => state.auth);

	const location = useLocation();
	const navigate = useNavigate();

	const [ templatesData, setTemplatesData ] = useState(null);
	const [ pagesNum, setPagesNum ] = useState(1);
	const [ templatesNum, setTemplatesNum ] = useState(0);

	const [ sortMethods, setSortMethods ] = useState([
		{ value: '-modifiedOn', label: dict.sortOptions.lastModified },
		{ value: 'title', label: dict.sortOptions.template },
		{ value: '-title', label: dict.sortOptions.templateReverse },
		CanHandleDepartments(userRole, mode) && { value: 'department', label: dict.sortOptions.department },
		CanHandleDepartments(userRole, mode) && { value: '-department', label: dict.sortOptions.departmentReverse },
	].filter(Boolean));

	const [ sortOn, setSortOn ] = useState(sortMethods[0]);
	const [ isLoading, setIsLoading ] = useState(true);

	const { search: locationSearch, pathname: locationPathName } = location;

	const changeSort = useCallback((element) => {
		const searchParams = queryString.parse(locationSearch);
		const copiedSearchParams = Object.assign({}, searchParams);

		setSortOn(element);
		navigate(`${locationPathName}?${parseObjectToQuery({ ...copiedSearchParams, ord: element.value })}`);
	}, [ setSortOn, locationSearch, locationPathName ]);

	useEffect(() => {
		const searchParams = queryString.parse(location.search);
		const copiedSearchParams = Object.assign({}, searchParams);

		if (copiedSearchParams.q) {
			const newSortMethods = [
				{ value: '-accuracy', label: dict.sortOptions.accuracy },
				{ value: '-modifiedOn', label: dict.sortOptions.lastModified },
				{ value: 'title', label: dict.sortOptions.template },
				{ value: '-title', label: dict.sortOptions.templateReverse },
				CanHandleDepartments(userRole, mode) && { value: 'department', label: dict.sortOptions.department },
				CanHandleDepartments(userRole, mode) && { value: '-department', label: dict.sortOptions.departmentReverse },
			].filter(Boolean);
			setSortMethods(newSortMethods);
		} else {
			const newSortMethods = [
				{ value: '-modifiedOn', label: dict.sortOptions.lastModified },
				{ value: 'title', label: dict.sortOptions.template },
				{ value: '-title', label: dict.sortOptions.templateReverse },
				CanHandleDepartments(userRole, mode) && { value: 'department', label: dict.sortOptions.department },
				CanHandleDepartments(userRole, mode) && { value: '-department', label: dict.sortOptions.departmentReverse },
			].filter(Boolean);
			setSortMethods(newSortMethods);
		}
	}, [ location, dict ]);

	useEffect(() => {
		const searchParams = queryString.parse(location.search);
		const copiedSearchParams = Object.assign({}, searchParams);

		if (copiedSearchParams.ord) {
			setSortOn(sortMethods.find((el) => { return el.value === copiedSearchParams.ord; }));
		}  else {
			setSortOn(sortMethods[0]);
		}
	}, [ sortMethods ]);

	useEffect(() => {
		const qu = queryString.parse(location.search);
		if (pagesNum !== 0 && (pagesNum < parseInt(qu.page))) {
			navigate(`${location.pathname}?${parseObjectToQuery({ ...qu, page: pagesNum })}`, { replace: true });
		}
	}, [ pagesNum, navigate ]);

	const fetchData = ({ data }) => {
		if (data) {
			setTemplatesData(data.letterTemplates);
			setPagesNum(parseInt(data.pages));
			setTemplatesNum(data.hits);
		}
	};

	const reloadLetterTemplatesList = useCallback(() => {
		const searchParams = queryString.parse(location.search);

		setIsLoading(true);
		api.get(`/letter-templates?${new URLSearchParams(searchParams).toString()}`).then(res => {
			fetchData(res);
			setIsLoading(false);
		});
	});

	const onConfirmDeleteTemplate = async (templateToRemoveId) => {
		await api.delete(`/letter-templates/${templateToRemoveId}`);
		reloadLetterTemplatesList();
		return;
	};

	useEffect(() => {
		reloadLetterTemplatesList();
	}, [ location ]);

	return (
		<div className={ styles['letter-templates__wrapper'] }>
			<Header narrow={ false } />
			<main className={ styles['letter-templates__main'] }>
				<div className={ styles['letter-templates__header--wrapper'] }>
					<h1 className={ styles['letter-templates__header'] }>
						{dict.templates.title}
						{templatesNum > 0 && (
							<span className={ styles['letter-templates__header--number'] }>&nbsp;({templatesNum})</span>)}
					</h1>
					<div
						className={ styles['letter-templates__header--how-to-create'] }
					>
						<TooltipIconText
							color="#0066D3"
							textColor="#01366F"
							text={ dict.templates.howToCreateTemplate } 
							tooltipText={ dict.templates.howToCreateTemplateTooltip }
						/>
					</div>
				</div>
				<div className={ styles['letter-templates__description'] }>{dict.templates.description}</div>
				<LetterTemplatesSearchFilters />
				<Pagination
					pagesNum={ pagesNum }
					sortingOptions={ sortMethods }
					sortingValue={ sortOn }
					sortingOnChange={ changeSort }
				>
					{/*<LetterTemplatesContainer
						templateData={ templatesData }
						sortOn={ sortOn }
						setSortOn={ setSortOn }
						sortMethods={ sortMethods }
						isLoading={ isLoading }
						onConfirmDeleteTemplate={ onConfirmDeleteTemplate }
						reloadLetterTemplatesList={ reloadLetterTemplatesList }
					/>*/}
					<LetterTemplateCardsContainer
						templateData={ templatesData }
						isLoading={ isLoading }
						onConfirmDeleteTemplate={ onConfirmDeleteTemplate }
					/>
				</Pagination>
			</main>

			<Footer narrow={ false }/>
		</div>
	);
};
export default Templates;